import { create } from "zustand";

import { getCookie, removeCookie, setCookie } from "@/lib/cookies";

const THEME_COOKIE = "vite-ui-theme";
export const THEMES = ["dark", "light", "system"] as const;
export type Theme = (typeof THEMES)[number];

type ThemeState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resetTheme: () => void;
};

const initial = (): Theme => {
  if (typeof document === "undefined") return "system";
  const fromHtml = document.documentElement.classList.contains("dark")
    ? "dark"
    : document.documentElement.classList.contains("light")
      ? "light"
      : null;
  if (fromHtml) return fromHtml;
  const cookie = getCookie(THEME_COOKIE);
  if (cookie && (THEMES as readonly string[]).includes(cookie)) return cookie as Theme;
  return "system";
};

const applyTheme = (theme: Theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  const resolved =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;
  root.classList.add(resolved);
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", resolved === "dark" ? "#020817" : "#fff");
  }
};

export const useThemeStore = create<ThemeState>((set) => ({
  theme: initial(),
  setTheme: (theme) => {
    setCookie(THEME_COOKIE, theme, { maxAge: 60 * 60 * 24 * 365 });
    applyTheme(theme);
    set({ theme });
  },
  resetTheme: () => {
    removeCookie(THEME_COOKIE);
    applyTheme("system");
    set({ theme: "system" });
  },
}));

if (typeof window !== "undefined") {
  applyTheme(useThemeStore.getState().theme);
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (useThemeStore.getState().theme === "system") {
      applyTheme("system");
    }
  });
}
